import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Typography, Space, message, Popconfirm, Descriptions, Tabs } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, GlobalOutlined, EyeOutlined } from '@ant-design/icons';
import { Importer } from '../types.ts';
import { importerAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

const { Title } = Typography;
const { Search } = Input;
const { TabPane } = Tabs;

const Importers: React.FC = () => {
  const { canWrite } = useAuth();
  const canEdit = canWrite('importers');
  const [importers, setImporters] = useState<Importer[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingImporter, setEditingImporter] = useState<Importer | null>(null);
  const [selectedImporter, setSelectedImporter] = useState<Importer | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form] = Form.useForm();

  const fetchImporters = async (search?: string) => {
    setLoading(true);
    try {
      const data = await importerAPI.getImporters(search);
      setImporters(data);
    } catch (error) {
      message.error('Errore nel caricamento importatori');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImporters();
  }, []);

  const handleAdd = () => {
    setEditingImporter(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (importer: Importer) => {
    setEditingImporter(importer);
    form.setFieldsValue(importer);
    setModalVisible(true);
  };

  const handleView = (importer: Importer) => {
    setSelectedImporter(importer);
    setDetailModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await importerAPI.deleteImporter(id);
      message.success('Importatore eliminato con successo');
      fetchImporters(searchTerm);
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nell\'eliminazione dell\'importatore');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingImporter) {
        await importerAPI.updateImporter(editingImporter.id, values);
        message.success('Importatore aggiornato con successo');
      } else {
        await importerAPI.createImporter(values);
        message.success('Importatore creato con successo');
      }
      setModalVisible(false);
      form.resetFields();
      fetchImporters(searchTerm);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Errore nell\'operazione';
      message.error(errorMessage);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    fetchImporters(value);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
    },
    {
      title: 'Ragione Sociale',
      dataIndex: 'ragione_sociale',
      key: 'ragione_sociale',
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: 'Città',
      dataIndex: 'citta',
      key: 'citta',
      render: (text: string) => text || '-',
    },
    {
      title: 'Paese',
      dataIndex: 'paese',
      key: 'paese',
      render: (text: string) => text || '-',
    },
    {
      title: 'Partita IVA',
      dataIndex: 'partita_iva',
      key: 'partita_iva',
      render: (text: string) => text || '-',
    },
    {
      title: 'Codice EORI',
      dataIndex: 'codice_eori',
      key: 'codice_eori',
      render: (text: string) => text || '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text: string) => text || '-',
    },
    {
      title: 'Telefono',
      dataIndex: 'telefono',
      key: 'telefono',
      render: (text: string) => text || '-',
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 150,
      render: (_, record: Importer) => (
        <Space>
          <Button
            type="default"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            title="Visualizza dettagli"
          />
          {canEdit && (
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          )}
          {canEdit && (
            <Popconfirm
              title="Sei sicuro di voler eliminare questo importatore?"
              onConfirm={() => handleDelete(record.id)}
              okText="Sì"
              cancelText="No"
            >
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>
          <GlobalOutlined style={{ marginRight: 8 }} />
          Gestione Importatori
        </Title>
        {canEdit && (<Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Nuovo Importatore
        </Button>)}
      </div>

      <div style={{ marginBottom: 16 }}>
        <Search
          placeholder="Cerca per ragione sociale, email, telefono, EORI o P.IVA..."
          allowClear
          enterButton={<SearchOutlined />}
          size="large"
          onSearch={handleSearch}
          style={{ maxWidth: 500 }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={importers}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1200 }}
      />

      {/* Modal Creazione/Modifica */}
      <Modal
        title={editingImporter ? 'Modifica Importatore' : 'Nuovo Importatore'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        destroyOnClose
        width={1000}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Tabs defaultActiveKey="1">
            <TabPane tab="Dati Identificativi" key="1">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item
                  name="ragione_sociale"
                  label="Ragione Sociale / Nome e Cognome"
                  rules={[{ required: true, message: 'Ragione sociale richiesta' }]}
                  style={{ gridColumn: '1 / -1' }}
                >
                  <Input placeholder="Denominazione legale dell'azienda o nome completo" />
                </Form.Item>
                
                <Form.Item
                  name="indirizzo"
                  label="Indirizzo Completo"
                >
                  <Input placeholder="Via, numero civico" />
                </Form.Item>
                
                <Form.Item
                  name="cap"
                  label="CAP"
                >
                  <Input placeholder="Codice postale" />
                </Form.Item>
                
                <Form.Item
                  name="citta"
                  label="Città"
                >
                  <Input placeholder="Città" />
                </Form.Item>
                
                <Form.Item
                  name="provincia"
                  label="Provincia"
                >
                  <Input placeholder="Provincia" />
                </Form.Item>
                
                <Form.Item
                  name="paese"
                  label="Paese"
                >
                  <Input placeholder="Paese" />
                </Form.Item>
                
                <Form.Item
                  name="partita_iva"
                  label="Partita IVA"
                >
                  <Input placeholder="Partita IVA" />
                </Form.Item>
                
                <Form.Item
                  name="codice_fiscale"
                  label="Codice Fiscale"
                >
                  <Input placeholder="Codice fiscale" />
                </Form.Item>
                
                <Form.Item
                  name="codice_eori"
                  label="Codice EORI"
                >
                  <Input placeholder="Codice EORI (Economic Operator Registration and Identification)" />
                </Form.Item>
                
                <Form.Item
                  name="telefono"
                  label="Telefono"
                >
                  <Input placeholder="Numero di telefono" />
                </Form.Item>
                
                <Form.Item
                  name="email"
                  label="Email"
                >
                  <Input type="email" placeholder="Indirizzo email" />
                </Form.Item>
                
                <Form.Item
                  name="sito_web"
                  label="Sito Web"
                >
                  <Input placeholder="URL sito web (opzionale)" />
                </Form.Item>
              </div>
            </TabPane>
            
            <TabPane tab="Contatto Referente" key="2">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item
                  name="nome_referente"
                  label="Nome e Cognome Referente"
                >
                  <Input placeholder="Nome e cognome del referente" />
                </Form.Item>
                
                <Form.Item
                  name="ruolo_referente"
                  label="Ruolo"
                >
                  <Input placeholder="Ruolo del referente" />
                </Form.Item>
                
                <Form.Item
                  name="telefono_referente"
                  label="Telefono Diretto"
                >
                  <Input placeholder="Numero di telefono diretto" />
                </Form.Item>
                
                <Form.Item
                  name="email_referente"
                  label="Email Diretta"
                >
                  <Input type="email" placeholder="Email diretta del referente" />
                </Form.Item>
              </div>
            </TabPane>
            
            <TabPane tab="Dati Bancari" key="3">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item
                  name="nome_banca"
                  label="Nome della Banca"
                >
                  <Input placeholder="Nome dell'istituto bancario" />
                </Form.Item>
                
                <Form.Item
                  name="iban"
                  label="IBAN"
                >
                  <Input placeholder="Codice IBAN" />
                </Form.Item>
                
                <Form.Item
                  name="bic_swift"
                  label="BIC/SWIFT"
                >
                  <Input placeholder="Codice BIC/SWIFT" />
                </Form.Item>
              </div>
            </TabPane>
          </Tabs>
        </Form>
      </Modal>

      {/* Modal Dettagli */}
      {selectedImporter && (
        <Modal
          title={`Dettagli Importatore: ${selectedImporter.ragione_sociale}`}
          open={detailModalVisible}
          onCancel={() => setDetailModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setDetailModalVisible(false)}>
              Chiudi
            </Button>
          ]}
          width={900}
        >
          <Tabs defaultActiveKey="1">
            <TabPane tab="Dati Identificativi" key="1">
              <Descriptions bordered column={2}>
                <Descriptions.Item label="Ragione Sociale" span={2}>
                  {selectedImporter.ragione_sociale}
                </Descriptions.Item>
                <Descriptions.Item label="Indirizzo">
                  {selectedImporter.indirizzo || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="CAP">
                  {selectedImporter.cap || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Città">
                  {selectedImporter.citta || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Provincia">
                  {selectedImporter.provincia || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Paese">
                  {selectedImporter.paese || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Partita IVA">
                  {selectedImporter.partita_iva || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Codice Fiscale">
                  {selectedImporter.codice_fiscale || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Codice EORI" span={2}>
                  {selectedImporter.codice_eori || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Telefono">
                  {selectedImporter.telefono || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {selectedImporter.email || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Sito Web" span={2}>
                  {selectedImporter.sito_web || '-'}
                </Descriptions.Item>
              </Descriptions>
            </TabPane>
            
            <TabPane tab="Contatto Referente" key="2">
              <Descriptions bordered column={2}>
                <Descriptions.Item label="Nome Referente">
                  {selectedImporter.nome_referente || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Ruolo">
                  {selectedImporter.ruolo_referente || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Telefono Diretto">
                  {selectedImporter.telefono_referente || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Email Diretta">
                  {selectedImporter.email_referente || '-'}
                </Descriptions.Item>
              </Descriptions>
            </TabPane>
            
            <TabPane tab="Dati Bancari" key="3">
              <Descriptions bordered column={2}>
                <Descriptions.Item label="Nome Banca">
                  {selectedImporter.nome_banca || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="IBAN">
                  {selectedImporter.iban || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="BIC/SWIFT" span={2}>
                  {selectedImporter.bic_swift || '-'}
                </Descriptions.Item>
              </Descriptions>
            </TabPane>
            
            <TabPane tab="Info Sistema" key="4">
              <Descriptions bordered column={2}>
                <Descriptions.Item label="Data Creazione">
                  {new Date(selectedImporter.created_at).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Ultimo Aggiornamento">
                  {new Date(selectedImporter.updated_at).toLocaleString()}
                </Descriptions.Item>
              </Descriptions>
            </TabPane>
          </Tabs>
        </Modal>
      )}
    </div>
  );
};

export default Importers;